import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Form, InputGroup, Modal, OverlayTrigger, Popover, Spinner, Table } from "react-bootstrap";
import { ApplicationState } from "../../../../../store";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import {
  CoachTeacherRelationshipRequest,
  SupervisorEducatorRelationshipRequest,
  TeacherInfo,
} from "../../../../../store/onboarding/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faSearch, faUser } from "@fortawesome/free-solid-svg-icons";
import ModalCloseButton from "../../third-step/group-students/common/ModalCloseButton";
import { getFullName } from "../../../../../utils/NamesUtils";
import LoadingIndicator from "../../LoadingIndicator";
import { CustomCheckbox } from "../evidence-tab/conduct-interview/CustomCheckbox";
import {
  addEducatorsToSupervisors,
  addTeachersToCoaches,
  hideAssignEducatorsToEmployeeModal,
  openTeacherDetailsModal,
} from "../../../../../store/onboarding/actions";
import { UserAssignmentRole } from "../../../../../store/auth/types";
import { toastr } from "react-redux-toastr";
import BootstrapTable, { SortOrder, TableChangeState, TableChangeType } from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

type StateProps = {
  showModal: boolean;
  teachersRoster: Array<TeacherInfo>;
  selectedEmployee?: TeacherInfo;
  isLoading: {
    getTeachers: boolean;
    addTeachersToCoaches: boolean;
    addEducatorsToSupervisors: boolean;
  };
};

type DispatchProps = {
  openTeacherDetailsModal: () => any;
  hideAssignEducatorsToCoachModal: () => any;
  addTeachersToCoaches: (
    relationships: Array<CoachTeacherRelationshipRequest>
  ) => any;
  addEducatorsToSupervisors: (
    relationships: Array<SupervisorEducatorRelationshipRequest>
  ) => any;
};

type Props = StateProps & DispatchProps;

const AssignEducatorsToEmployeeModal: FunctionComponent<Props> = ({
  showModal,
  isLoading,
  teachersRoster,
  selectedEmployee,
  openTeacherDetailsModal,
  addTeachersToCoaches,
  addEducatorsToSupervisors,
  hideAssignEducatorsToCoachModal,
}) => {
  const [teachersFilter, setTeachersFilter] = useState("");
  const [filteredTeachersRoster, setFilteredTeachersRoster] = useState<
    Array<TeacherInfo>
  >([]);
  const [assignedTeacherIds, setAssignedTeacherIds] = useState<Array<number>>(
    []
  );
  
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | undefined>(undefined);
  const selectedEducators = useMemo(
    () =>
      teachersRoster.filter((t) => assignedTeacherIds.includes(t.user.id)),
    [teachersRoster, assignedTeacherIds]
  );

  useEffect(() => {
    setFilteredTeachersRoster(teachersRoster.filter(filterTeachers()));
  }, [teachersRoster]);

  const isCoach = useMemo(
    () =>
      selectedEmployee?.user.profile.current_assignment?.role ===
      UserAssignmentRole.COACH,
    [selectedEmployee]
  );

  const handleShowModal = () => {
    setTeachersFilter("");
    setAssignedTeacherIds([]);
  };

  const handleHideModal = () => {
    hideAssignEducatorsToCoachModal();
    openTeacherDetailsModal();
  };

  const filterTeachers = (value: string = teachersFilter || "") => (
    teacher: TeacherInfo
  ) => {
    return (
      teacher.user.profile.current_role === UserAssignmentRole.TEACHER &&
      (!value ||
        getFullName(teacher.user)
          .toLowerCase()
          .includes(value.toLowerCase()))
    );
  };

  const onTeacherFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setTeachersFilter(value);
    setFilteredTeachersRoster(teachersRoster.filter(filterTeachers(value)));
  };

  const handleTeachersAssign = () => {
    const selectedEmployeeId = selectedEmployee!.user.id;

    if (isCoach) {
      const relationships: Array<CoachTeacherRelationshipRequest> = assignedTeacherIds.map(
        (id) => ({
          coach: selectedEmployeeId,
          teacher: id,
        })
      );

      addTeachersToCoaches(relationships).then(
        () => {
          toastr.success("Success", "Educator(s) assigned successfully."); 
          handleHideModal();
        },
        (error: string) => toastr.error("Failed to assign the educators", error)
      );
    } else {
      const relationships: Array<SupervisorEducatorRelationshipRequest> = assignedTeacherIds.map(
        (id) => ({
          supervisor: selectedEmployeeId,
          user: id,
        })
      );
      addEducatorsToSupervisors(relationships).then(
        () =>  {
          toastr.success("Success", "Coaches(s) assigned successfully."); 
          handleHideModal();
        },
        (error: string) => toastr.error("Failed to assign the coaches", error)
      );
    }
  };

  const handleAssignTeacherChange = (teacherId: number) => {
    setAssignedTeacherIds((ids) =>
      ids.includes(teacherId)
        ? ids.filter((id) => id != teacherId)
        : [...ids, teacherId]
    );
  };

  const columns = useMemo(
        () => [
          {
            dataField: 'selectCheckbox',
            text: 'SELECT',
            headerStyle: { width: '20%' },
          },
          {
            dataField: 'fullName',
            text: 'EDUCATORS',
            sort: true,
            headerStyle: { width: '40%' },
          },
          {
            dataField: 'email',
            text: 'EMAIL',
            sort: true,
            headerStyle: { width: '40%' },
          },  
        ],
        []
      );
    
      const options = useMemo(
        () => ({
          sizePerPageList: [
            {
              text: "10",
              value: 10,
            },
            {
              text: "20",
              value: 20,
            },
            {
              text: "30",
              value: 30,
            },
          ],
        }),
        []
      );
    
  const collator = useMemo(
      () => new Intl.Collator(undefined, { numeric: true, sensitivity: "base" }),
      []
    );

  const educatorsRosterRows = useMemo(() => {
    return filteredTeachersRoster.map((teacher) => {
      return {
        id: teacher.user.id,
        selectCheckbox: (
          <Form.Check
            label
            custom
            className="observedItemCheckbox"
            type="checkbox"
            onChange={() => handleAssignTeacherChange(teacher.user.id)}
            checked={assignedTeacherIds.includes(teacher.user.id)}
          />
        ),
        fullName: getFullName(teacher.user),
        email: teacher.user.email.length > 50 
          ? <span className="d-inline-block text-truncate" title={teacher.user.email} style={{maxWidth: "400px"}}>{teacher.user.email}</span>
          : teacher.user.email
      }
    });
  }, [filteredTeachersRoster, assignedTeacherIds]);

  const sortedEducatorsRosterRows = useMemo(() => {
    const originalData = [...educatorsRosterRows];
    if (!sortField || !sortOrder) {
      return originalData;
    }

    const sortedData = [...originalData].sort((a: any, b: any) => {
      let aValue = a?.[sortField];
      let bValue = b?.[sortField];

      const isEmpty = (val: any) => val === null || val === undefined || val === "";
      if (isEmpty(aValue) && !isEmpty(bValue)) return 1;
      if (!isEmpty(aValue) && isEmpty(bValue)) return -1;
      if (isEmpty(aValue) && isEmpty(bValue)) return 0;

      aValue = aValue?.toString().trim();
      bValue = bValue?.toString().trim();

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? collator.compare(aValue, bValue)
          : collator.compare(bValue, aValue);
      }
      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });

    return sortedData;
  }, [educatorsRosterRows, sortField, sortOrder, collator]);

  const handleTableChange = (
    type: TableChangeType,
    { sortField: clickedField }: TableChangeState<any>
  ) => {
    if (type === "sort") {
      let newSortField: string | undefined = clickedField;
      let newSortOrder: "asc" | "desc" | undefined = "asc";

      if (sortField === clickedField) {
        if (sortOrder === "asc") {
          newSortOrder = "desc";
        } else if (sortOrder === "desc") {
          newSortField = undefined;
          newSortOrder = undefined;
        }
      }

      setSortField(newSortField);
      setSortOrder(newSortOrder);
    }
  };

  useEffect(() => {
    const classListDesc = document.getElementsByClassName('caret-4-desc') as HTMLCollectionOf<HTMLElement>;
    const classListAsc = document.getElementsByClassName('caret-4-asc') as HTMLCollectionOf<HTMLElement>;

    const setDisplay = (elements: HTMLCollectionOf<HTMLElement>, display: string) => {
      Array.from(elements).forEach((el) => {
        el.style.display = display;
      });
    };

    if (sortOrder === 'asc') {
      setDisplay(classListAsc, 'inline-block');
      setDisplay(classListDesc, 'none');
    } else if (sortOrder === 'desc') {
      setDisplay(classListDesc, 'inline-block');
      setDisplay(classListAsc, 'none');
    } else if (!sortField && !sortOrder) {
      setDisplay(classListAsc, 'none');
      setDisplay(classListDesc, 'none');
    }
  }, [sortField, sortOrder]);

  const noDataIndicator = useMemo(() => {
    if (isLoading.getTeachers) {
      return <LoadingIndicator type="table" rows={10} columns={3} />;
    }
    return "No matching educator(s)";
  }, [isLoading.getTeachers]);

    

  return (
    <Modal
      animation={false}
      backdropClassName="customDarkModalBackdrop in"
      show={showModal}
      onShow={handleShowModal}
      onHide={handleHideModal}
      size="lg"
      centered
      dialogClassName="modal-dialog-no-scroll"
    >
      <Modal.Header className="purpleModalHeader">
        <Modal.Title>{`Assign Educator(s) to ${selectedEmployee?.user.first_name} ${selectedEmployee?.user.last_name}`}</Modal.Title>
        <ModalCloseButton onClose={handleHideModal} />
      </Modal.Header>
      <Modal.Body>
        <h2 className="font-weight-bold">
          Assign one or more educators to this coach to collaborate on cases.{" "}
        </h2>
        <div className="mt-2">
          <span className="alert alert-warning py-1 px-1"><span className="font-weight-bold">Note:</span> The coach cannot invite other educators at this time.</span>
        </div>

        <div className="mb-3 mt-4 row justify-content-between align-items-end">
          <div className="col d-flex align-items-end">
            <InputGroup className="filterSearchDesktop mr-2" style={{width:"300px"}}>
              <InputGroup.Prepend>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
              </InputGroup.Prepend>
              <Form.Control
                type="text"
                placeholder={`Search`}
                value={teachersFilter || ""}
                onChange={onTeacherFilterChange}
                className="filterSearchHeight"
              />
            </InputGroup>
            {assignedTeacherIds.length ? (
              <span className="ml-2">
                {assignedTeacherIds.length} Educator(s) Selected
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Popover id="selected-educators-tooltip">
                      {assignedTeacherIds.length < 10 ? (
                        <ul className="p-2 m-0 list-unstyled">
                          {selectedEducators.map((t) => (
                            <li key={t.user.id}>{getFullName(t.user)}</li>
                          ))}
                        </ul>
                      ) : (
                        selectedEducators.map((t) => getFullName(t.user)).join(", ") || ""
                      )}
                    </Popover>
                  }
                >
                  <span className="selectedStudentsCount">
                    <FontAwesomeIcon
                      icon={faUser}
                      className="ml-2 pointer rotate-180"
                    />
                  </span>
                </OverlayTrigger>
              </span>
            ) : (
              ""
            )}
          </div>
        </div>
        <hr/>
        <div>
          <BootstrapTable
            remote={{ sort: true }}
            classes="rosterTable"
            wrapperClasses="table-responsive mb-3 sticky-table-header"
            keyField="id"
            bordered={false}
            bootstrap4
            data={sortedEducatorsRosterRows}
            columns={columns}
            pagination={sortedEducatorsRosterRows.length > 10 ? paginationFactory(options) : undefined}
            noDataIndication={noDataIndicator}
            onTableChange={handleTableChange}
            sort={sortField && sortOrder ? {
              dataField: sortField,
              order: sortOrder,
            } : undefined }
          />
        </div>
        <hr />
        <div className="modalActions">
          <button className="whiteBtnSm" onClick={handleHideModal}>
            Back
          </button>
          <button
            className="blueBtnSm"
            disabled={!assignedTeacherIds.length}
            onClick={handleTeachersAssign}
          >
            ASSIGN EDUCATOR(S){" "}
            {(isLoading.addTeachersToCoaches ||
              isLoading.addEducatorsToSupervisors) && (
              <Spinner animation="border" size="sm" />
            )}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = ({ onboarding }: ApplicationState): StateProps => {
  const alreadyAssignedUsers = onboarding.districtRelationships.map(
    (user) => user.child.user.id
  );
  return {
    showModal: onboarding.modalsState.assignEducatorsToEmployee,
    selectedEmployee: onboarding.selectedTeacher,
    teachersRoster: onboarding.teachersRoster.filter(
      (t) => !alreadyAssignedUsers.includes(t.user.id)
    ),
    isLoading: {
      getTeachers: onboarding.isLoading.getTeachers,
      addTeachersToCoaches: onboarding.isLoading.addTeachersToCoaches,
      addEducatorsToSupervisors: onboarding.isLoading.addEducatorsToSupervisors,
    },
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return bindActionCreators(
    {
      openTeacherDetailsModal: openTeacherDetailsModal,
      hideAssignEducatorsToCoachModal: hideAssignEducatorsToEmployeeModal,
      addTeachersToCoaches: addTeachersToCoaches,
      addEducatorsToSupervisors: addEducatorsToSupervisors,
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AssignEducatorsToEmployeeModal);
